import { ImageScaleView, View } from '@play-co/timestep-core/ui';
import StateObserver, { user, now } from 'src/StateObserver';
import { useCache } from 'src/lib/utils';
import * as match2TournamentGetters from 'src/replicant/match2/tournament/match2-tournament.getters';
import { TournamentType } from 'src/replicant/match2/tournament/match2-tournament.ruleset';
import { duration } from 'src/replicant/utils/duration';
import features from 'src/replicant/ruleset/features';
import { downscale } from 'src/conf/scale';

const tournamentAssetPath = 'assets/ui/match2/tournament';
const flyingHighAssetPath = `${tournamentAssetPath}/flyinghigh`;

type TournamentAssetLocale = 'en' | 'ja';
type StreakPopupBannerState = 'ongoing' | 'claim';

export function isFlyingHighRedesignTournament(type: TournamentType = 'star') {
  return type === 'streak' && features(user()).flyingHighRedesign;
}

export function getTournamentTitleImage(
  type: TournamentType,
  locale: TournamentAssetLocale,
) {
  if (isFlyingHighRedesignTournament(type)) {
    return `${flyingHighAssetPath}/title_${locale}.png`;
  }

  return `${tournamentAssetPath}/${type}_tournament_title_${locale}.png`;
}

export function getStreakTournamentBannerImage(state: StreakPopupBannerState) {
  if (isFlyingHighRedesignTournament('streak')) {
    return `${flyingHighAssetPath}/banner_${state === 'ongoing' ? 1 : 2}.png`;
  }

  return `${tournamentAssetPath}/streak_tournament_banner${
    state === 'ongoing' ? 1 : 2
  }.png`;
}

export function getTournamentHeroImage(type: TournamentType) {
  if (isFlyingHighRedesignTournament(type)) {
    return `${flyingHighAssetPath}/hero.png`;
  }

  return `${tournamentAssetPath}/${type}_tournament_hero.png`;
}

export function getTournamentIconImage(type: TournamentType) {
  if (isFlyingHighRedesignTournament(type)) {
    return `${flyingHighAssetPath}/icon.png`;
  }

  return `${tournamentAssetPath}/${type}_tournament_icon.png`;
}

export function getTournamentRankImage(type: TournamentType) {
  if (isFlyingHighRedesignTournament(type)) {
    return 'assets/ui/match2/tournament/flyinghigh/medal.png';
  }

  return 'assets/ui/match2/tournament/streak_tournament_result_rocket.png';
}

export function getStreakPopupTitleLayout(locale: TournamentAssetLocale) {
  if (!isFlyingHighRedesignTournament('streak')) {
    return {
      width: locale === 'en' ? 455 : 386,
      height: locale === 'en' ? 59 : 65,
      y: 190,
    };
  }

  return {
    width: locale === 'en' ? downscale(676) : downscale(496),
    height: locale === 'en' ? downscale(230) : downscale(218),
    y: 130,
  };
}

export function getStreakPopupClaimTitleLayout() {
  if (isFlyingHighRedesignTournament('streak')) {
    return {
      y: 80,
      scale: 0.85,
    };
  }
  return { y: 70 };
}

/**
 * Get the title dimensions for a tournament information popup.
 *
 * @param type Tournament type shown by the popup.
 * @param locale Locale used to select the title asset.
 * @returns Render dimensions for the selected tournament title.
 */
export function getInfoPopupTitleLayout(
  type: TournamentType,
  locale: TournamentAssetLocale,
) {
  // Render the new Star Tournament assets at the game's native resolution.
  if (type === 'star') {
    return {
      width: locale === 'en' ? downscale(583) : downscale(485),
      height: locale === 'en' ? downscale(199) : downscale(200),
    };
  }

  // Keep the legacy Streak Tournament title dimensions unchanged.
  if (!isFlyingHighRedesignTournament(type)) {
    return {
      width: locale === 'en' ? 586 : 367,
      height: locale === 'en' ? 79 : 125,
    };
  }

  // Render redesigned Streak Tournament titles at the game's native resolution.
  return {
    width: locale === 'en' ? downscale(676) : downscale(496),
    height: locale === 'en' ? downscale(230) : downscale(218),
  };
}

export function createUiTournamentHeader(
  parent: View,
  type: TournamentType = 'star',
  x = 0,
  y = 0,
) {
  return new ImageScaleView({
    superview: parent,
    image: `assets/ui/match2/tournament/${type}_tournament_header.png`,
    width: 488,
    height: 188,
    scaleMethod: 'contain',
    x,
    y,
    canHandleEvents: false,
    centerOnOrigin: true,
    centerAnchor: true,
  });
}

export function getTournament(
  eventId = match2TournamentGetters.getTournamentPeriod(user(), now())?.eventId,
) {
  if (!eventId) return undefined;

  return useCache(
    async () => {
      // No need to fetch if the event is not in the user's state.
      if (!user().match2.tournament.events[eventId]) return undefined;

      // Fetch the tournament event data.
      const data =
        await StateObserver.replicant.asyncGetters.getMatch2TournamentEvent({
          eventId,
        });

      // Return undefined if the event is not found.
      if (!data) return undefined;

      // Append the eventId to the data.
      return { eventId, ...data };
    },
    `match2-tournament-${eventId}`,
    duration({ seconds: 20 }),
  );
}
